/* eslint-disable i18next/no-literal-string -- dev-only override panel restricted to local/test environments; not user-facing UI */
import { type ReactNode, useState } from "react";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { SidebarModalV2 } from "@vega-core/components/Modals/SidebarModal/SidebarModalV2";

import { OverrideRow, type TriStateValue } from "./OverrideRow";

type OverridePanelProps<Key extends string> = {
  title: string;
  dataTestIdPrefix: string;
  keys: Key[];
  headerContent?: ReactNode;
  overrides: Partial<Record<Key, boolean>>;
  effectiveValues: Partial<Record<Key, boolean>>;
  onClose: () => void;
  onSetOverride: (key: Key, value: boolean) => void;
  onClearOverride: (key: Key) => void;
  onClearAll: () => void;
  onSetAll: (value: boolean) => void;
};

export function OverridePanel<Key extends string>({
  title,
  dataTestIdPrefix,
  keys,
  headerContent,
  overrides,
  effectiveValues,
  onClose,
  onSetOverride,
  onClearOverride,
  onClearAll,
  onSetAll,
}: OverridePanelProps<Key>) {
  const [search, setSearch] = useState("");

  const needle = search.toLowerCase().trim();
  const filteredKeys =
    needle === ""
      ? keys
      : keys.filter((key) => key.toLowerCase().includes(needle));

  const activeOverrideCount = Object.keys(overrides).length;

  function handleSelect(key: Key, option: TriStateValue) {
    if (option === "default") {
      onClearOverride(key);

      return;
    }

    onSetOverride(key, option === "on");
  }

  return (
    <SidebarModalV2
      actionPanel={
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <div className="body-small-regular text-text/secondary">
              {activeOverrideCount === 0
                ? "No active overrides"
                : `${activeOverrideCount} active override${activeOverrideCount === 1 ? "" : "s"}`}
            </div>
            <ThemedButton
              dataTestId={`${dataTestIdPrefix}-reset-all`}
              disabled={activeOverrideCount === 0}
              isCompact
              onClick={onClearAll}
              variant="tertiary"
            >
              Reset all
            </ThemedButton>
          </div>
          <div className="flex items-center gap-2">
            <ThemedButton
              dataTestId={`${dataTestIdPrefix}-enable-all`}
              fullWidth
              isCompact
              onClick={() => onSetAll(true)}
              variant="secondary"
            >
              Force enable all
            </ThemedButton>
            <ThemedButton
              dataTestId={`${dataTestIdPrefix}-disable-all`}
              fullWidth
              isCompact
              onClick={() => onSetAll(false)}
              variant="secondary"
            >
              Force disable all
            </ThemedButton>
          </div>
        </div>
      }
      dataTestId={`${dataTestIdPrefix}-panel`}
      isOpen
      setIsOpen={onClose}
      title={title}
    >
      <div className="flex flex-col gap-4">
        {headerContent}
        <input
          className="body-small-regular border border-border/primary rounded p-2 bg-background/primary text-text/primary outline-none focus:border-brand-default"
          data-testid={`${dataTestIdPrefix}-search`}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search..."
          type="text"
          value={search}
        />
        <div className="flex flex-col divide-y divide-border/primary">
          {filteredKeys.length === 0 ? (
            <div className="py-4 body-small-regular text-text/secondary">
              No matches for &quot;{search}&quot;
            </div>
          ) : (
            filteredKeys.map((key) => (
              <OverrideRow
                dataTestIdPrefix={dataTestIdPrefix}
                effectiveValue={!!effectiveValues[key]}
                key={key}
                onSelect={(option) => handleSelect(key, option)}
                override={overrides[key]}
                rowKey={key}
              />
            ))
          )}
        </div>
      </div>
    </SidebarModalV2>
  );
}
